import { useEffect, useMemo } from 'react';

import { uniqBy } from 'lodash-es';

import { useI18n } from '@change-corgi/core-react/i18n';
import { useTracking } from '@change-corgi/core-react/tracking';

import type { DecisionMakerTargetedPetitionsItem } from 'src/app/pages/decisionMakerProfile/pageContext';
import {
	useDecisionMakerProfilePageData,
	useDMProfilePagePaginatedResponsesData,
	useDMProfilePagePaginatedTargetedPetitionsData,
} from 'src/app/pages/decisionMakerProfile/pageContext';
import { useQueryParamValue } from 'src/app/shared/hooks/location';
import { useTrackOnce } from 'src/app/shared/hooks/tracking';

type Result = ModelHookResult<
	{
		petitions: readonly DecisionMakerTargetedPetitionsItem[];
		hasNextPage: boolean;
		nextPageLoading: boolean;
		sectionHeader: string;
		showDmrlSeparator: boolean;
	},
	{
		onClickPetitionLink: (petitionId: string) => void;
		loadMore: () => void;
	}
>;

export function useTargetedPetitionList(): Result {
	const {
		data: { items, hasNextPage, nextPageLoading, totalCount: petitionsCount },
		actions: { loadMore },
	} = useDMProfilePagePaginatedTargetedPetitionsData();

	const {
		data: { totalCount: responsesCount },
	} = useDMProfilePagePaginatedResponsesData();

	const {
		data: {
			displayName,
			enrichedData,
			featureConfigs: { dmReputationLayer },
		},
	} = useDecisionMakerProfilePageData();

	const showDmrl = useQueryParamValue('showDmrl');
	const isDmrlEnabled = dmReputationLayer.enabled || showDmrl === 'true';
	const showDmrlSeparator = isDmrlEnabled && enrichedData !== null;

	const track = useTracking();
	const { translate, translatePlural } = useI18n();

	const onClickPetitionLink = (petitionId: string) => {
		void track('dm_profile_petition_click', { petition_id: petitionId });
	};

	// checks for responsesCount because its value deteremines
	// whether or not we show the statistics section
	const showPetitionCountInHeader = isDmrlEnabled || (!responsesCount && !!petitionsCount);
	const sectionHeader = showPetitionCountInHeader
		? translatePlural('corgi.decision_maker_profile.petitions_list.header_with_count', petitionsCount, {
				count: petitionsCount,
				displayName,
			})
		: translate('corgi.decision_maker_profile.petitions_list.header', { displayName });

	// Copied from useTargetedPetitionList:
	// some petitions are sometimes present in duplicate in the list returned by the API...
	const petitions = useMemo(() => uniqBy(items, 'id'), [items]);
	const trackOnce = useTrackOnce();
	useEffect(() => {
		if (petitions.length > 0) void trackOnce('dm_profile_petition_list_view');
	}, [petitions.length, trackOnce]);

	return {
		data: {
			petitions,
			hasNextPage,
			nextPageLoading,
			sectionHeader,
			showDmrlSeparator,
		},
		actions: {
			onClickPetitionLink,
			loadMore,
		},
	};
}
